import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import {
  DEFAULT_TTS_RATE,
  DEFAULT_TTS_VOICE_CHOICE,
  type TtsVoiceChoice,
} from '@/lib/readAloudSettings';

interface PinnedSegment {
  id: string | number;
  content?: string;
  segment_type: string;
  parent_id: string | number;
  qNo?: string | number;
  pinnedSegmentParentIdxs?: number[];
}

interface BookmarkedSegment {
  segment_id: string | number;
  question_id: string | number;
  topic_id: string | number;
  topic_idx: number;
  question_number: number;
}

interface SegmentHistoryItem {
  id?: string;
  child_segment_attributes?: Record<string, any>;
  [key: string]: any;
}

interface SegmentState {
  pinnedSegment: PinnedSegment | null;
  pinnedResourceSheet: boolean;
  bookmarkedSegments: BookmarkedSegment[];
  activeSegmentId: string | number | null;
  focusedSegmentId: string | number | null;
  focusedSegmentData: any;
  googleDriveAccessToken: string | null;
  activeMediaId: string | number | null;
  currentHistoryOfSegment: any;
  segmentDuplicateLoading: boolean;
  rearrangeMode: boolean;
  /** A rearrange move is saving. Leaving rearrange mode now would drop the
   *  cache surgery with the unmounted panel, so the exit waits for it. */
  rearrangeMovePending: boolean;
  /** Stamped every time a move in flight refuses an exit — the rail tells the
   *  user. A counter, not a boolean: two refusals in a row must both speak. */
  rearrangeExitBlockedAt: number;
  /** The card a rearrange move just placed — flashed by SegmentCard, short-lived. */
  lastMovedSegmentId: string | number | null;
  selectedTextForTTS: string | null;
  /** The read-aloud speed and voice the popup sets. This store takes no persist
   *  middleware, so a page refresh returns both to their default. */
  ttsRate: number;
  ttsVoice: TtsVoiceChoice;
  // Pin actions
  isPinned: (segmentId: string | number) => boolean;
  pinSegment: (segment: PinnedSegment | null) => void;
  unpinSegment: (segmentId: string | number) => void;
  pinResourceSheet: () => void;
  unpinResourceSheet: () => void;

  // Bookmark actions
  isBookmarked: (segmentId: string | number) => boolean;
  bookmarkSegment: (segment: BookmarkedSegment) => void;
  unbookmarkSegment: (segmentId: string | number) => void;

  // Active segment actions
  setActiveSegment: (segmentId: string | number | null) => void;
  setFocusedSegment: (segmentId: string | number | null, segmentData?: any) => void;
  setGoogleDriveAccessToken: (accessToken: string | null) => void;

  // Active media actions
  setActiveMediaId: (mediaId: string | number | null) => void;

  // Selected text for TTS actions
  setTextForTTS: (text: string | null) => void;

  // Read-aloud settings actions
  setTtsRate: (rate: number) => void;
  setTtsVoice: (voice: TtsVoiceChoice) => void;

  // History actions
  setSegmentHistory: (history: SegmentHistoryItem) => void;

  // Duplicate loading actions
  setSegmentDuplicateLoading: (loading: boolean) => void;

  // Rearrange mode actions
  setRearrangeMode: (rearrangeMode: boolean) => void;
  setRearrangeMovePending: (pending: boolean) => void;
  /** The rail consumes the refusal marker once it has told the teacher. */
  clearRearrangeExitBlocked: () => void;
  setLastMovedSegmentId: (segmentId: string | number | null) => void;
}

export const useSegmentStore = create<SegmentState>()(
  devtools((set, get) => ({
    pinnedSegment: null,
    pinnedResourceSheet: false,
    bookmarkedSegments: [],
    activeSegmentId: null,
    focusedSegmentId: null,
    focusedSegmentData: null,
    googleDriveAccessToken: null,
    activeMediaId: null,
    currentHistoryOfSegment: {},
    segmentDuplicateLoading: false,
    rearrangeMode: false,
    rearrangeMovePending: false,
    rearrangeExitBlockedAt: 0,
    lastMovedSegmentId: null,
    selectedTextForTTS: null,
    ttsRate: DEFAULT_TTS_RATE,
    ttsVoice: DEFAULT_TTS_VOICE_CHOICE,
    isPinned: (segmentId) => {
      const pinnedSegment = get().pinnedSegment;
      return pinnedSegment ? pinnedSegment.id === segmentId : false;
    },

    pinSegment: (segment) => {
      const isPinned = get().isPinned(segment?.id ?? 0);
      if (!isPinned) {
        set(() =>
          segment
            ? {
                pinnedSegment: segment,
                pinnedResourceSheet: false,
              }
            : {
                pinnedSegment: segment,
              }
        );
      }
    },

    unpinSegment: (segmentId) => {
      set((state) => ({
        pinnedSegment: state.pinnedSegment?.id === segmentId ? null : state.pinnedSegment,
      }));
    },

    pinResourceSheet: () => {
      set(() => ({
        pinnedResourceSheet: true,
        pinnedSegment: null,
      }));
    },

    unpinResourceSheet: () => {
      set(() => ({
        pinnedResourceSheet: false,
      }));
    },

    isBookmarked: (segmentId) => {
      return get().bookmarkedSegments.some((segment) => segment.segment_id === segmentId);
    },

    bookmarkSegment: (segment) => {
      const isBookmarked = get().isBookmarked(segment.segment_id);
      if (!isBookmarked) {
        set((state) => ({
          bookmarkedSegments: [...state.bookmarkedSegments, segment],
        }));
      }
    },

    unbookmarkSegment: (segmentId) => {
      set((state) => ({
        bookmarkedSegments: state.bookmarkedSegments.filter(
          (segment) => segment.segment_id !== segmentId
        ),
      }));
    },

    setActiveSegment: (segmentId) => {
      set({ activeSegmentId: segmentId });
    },
    setFocusedSegment: (segmentId, segmentData) => {
      set({ focusedSegmentId: segmentId, focusedSegmentData: segmentData ?? null });
    },
    setGoogleDriveAccessToken: (accessToken) => {
      set({ googleDriveAccessToken: accessToken });
    },

    setActiveMediaId: (mediaId) => {
      set({ activeMediaId: mediaId });
    },

    // Selected text for TTS actions
    setTextForTTS: (text) => {
      set({
        selectedTextForTTS: text,
      });
    },

    // Read-aloud settings actions
    setTtsRate: (rate) => {
      set({ ttsRate: rate });
    },
    setTtsVoice: (voice) => {
      set({ ttsVoice: voice });
    },

    // History actions
    setSegmentHistory: (history) => {
      set({
        currentHistoryOfSegment: history,
      });
    },

    // Rearrange mode actions
    setRearrangeMode: (rearrangeMode) => {
      // react-query drops a mutate() callback when its component unmounts, so
      // an exit mid-save loses the cache surgery and the page keeps the old
      // order until a reload (R-LOGIC-16). Refuse, and stamp the refusal.
      if (!rearrangeMode && get().rearrangeMovePending) {
        set({ rearrangeExitBlockedAt: Date.now() });
        return;
      }
      // The marker is a ONE-SHOT signal to the rail. Left standing, it fired
      // the toast on every later entry into rearrange mode, with no save in
      // flight (R-LOGIC-19). Every accepted mode change starts it clean.
      set({ rearrangeMode, rearrangeExitBlockedAt: 0 });
    },
    clearRearrangeExitBlocked: () => {
      set({ rearrangeExitBlockedAt: 0 });
    },
    setRearrangeMovePending: (pending) => {
      set({ rearrangeMovePending: pending });
    },
    setLastMovedSegmentId: (segmentId) => {
      set({ lastMovedSegmentId: segmentId });
    },

    // Duplicate loading actions
    setSegmentDuplicateLoading: (loading) => {
      set({ segmentDuplicateLoading: loading });
    },
  }))
);

export const selectCurrentHistoryOfSegment = (state: SegmentState): any =>
  state.currentHistoryOfSegment;

export const selectActiveMediaId = (state: SegmentState): any => state.activeMediaId;

export const selectIsPinnedSegment = (state: SegmentState): any => state.pinnedSegment !== null;

export const selectPinnedSegment = (state: SegmentState): PinnedSegment | null =>
  state.pinnedSegment;

export const selectPinnedResourceSheet = (state: SegmentState): boolean =>
  state.pinnedResourceSheet;

export const selectRearrangeMode = (state: SegmentState): boolean => state.rearrangeMode;

export const selectLastMovedSegmentId = (state: SegmentState) => state.lastMovedSegmentId;

export const selectFocusedSegmentId = (state: SegmentState) => state.focusedSegmentId;
export const selectFocusedSegmentData = (state: SegmentState) => state.focusedSegmentData;

export const selectTextForTTS = (state: SegmentState): string | null => state.selectedTextForTTS;

export const selectTtsRate = (state: SegmentState): number => state.ttsRate;

export const selectTtsVoice = (state: SegmentState): TtsVoiceChoice => state.ttsVoice;
